import { Node, type NodeProps } from '@xyflow/react';
import { useIntl } from 'react-intl';
import { SchemaFieldIcon } from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import { IconAlertTriangle } from '@rossum/ui/icons/tabler';
import { SvgIcon, useTheme } from '@rossum/ui/material';
import { BaseNode } from './BaseNode';
import { isComputedFieldType } from './computeCycles';
import { type FieldNodeData, NODE_HEIGHT } from './computeLayout';
import { fieldIconType } from './fieldIconType';

export const FieldNode = ({ id, data }: NodeProps<Node<FieldNodeData>>) => {
  const theme = useTheme();
  const intl = useIntl();
  const isExternal = data.fieldType === 'external';
  const iconType = fieldIconType(data.fieldType);

  const accentColor = isExternal
    ? theme.palette.error.main
    : theme.palette.primary.main;
  const accentLightColor = isExternal
    ? theme.palette.error.light
    : theme.palette.primary.light;

  const icon = isExternal ? (
    <SvgIcon fontSize="small" color="error">
      <IconAlertTriangle />
    </SvgIcon>
  ) : iconType ? (
    <SchemaFieldIcon
      type={iconType}
      fontSize="small"
      sx={{ color: theme.palette.text.secondary }}
    />
  ) : null;

  const tooltipTitle = isExternal
    ? intl.formatMessage(
        {
          id: 'containers.settings.queues.queue.overview.externalFieldTooltip',
        },
        { label: data.label }
      )
    : data.label;

  return (
    <BaseNode
      id={id}
      label={data.label}
      accentColor={accentColor}
      accentLightColor={accentLightColor}
      icon={icon}
      dashed={isExternal}
      minHeight={NODE_HEIGHT}
      tooltipTitle={tooltipTitle}
      tooltipEnterDelay={isExternal ? 0 : 500}
      warningMessage={isExternal ? tooltipTitle : undefined}
      hasComputedValue={isComputedFieldType(data.fieldType)}
      detailSections={[
        {
          label: intl.formatMessage({
            id: 'containers.settings.queues.queue.overview.node.dependencies',
          }),
          items: data.dependencies,
        },
      ]}
    />
  );
};
